import { Divider, LoadingOverlay, Tabs } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { TSDStatus } from '@e-origin/shared';

import { ContentLayout } from '@features/layout';
import { useTSD } from '@hooks/tsd';
import { getTSDVisibleTabs } from './getTSDVisibleTabs';
import { TSDViewHeader } from './TSDViewHeader';
import { useTSDViewTabs } from './useTSDViewTabs';

// eslint-disable-next-line max-statements
export const TSDView: React.FC = () => {
  const navigate = useNavigate();
  const { id, tabId } = useParams<{ id: string; tabId?: string }>() as any;

  const basePath = `/tsd/${id}`;

  const [disabled, setDisabled] = useState<boolean>(true);
  const { data: tsd, isLoading } = useTSD(id);

  const visibleTabs = useMemo(() => {
    if (!tsd) {
      return [];
    }

    return getTSDVisibleTabs({ tsd, disabled });
  }, [tsd, disabled]);

  const activeTab = useMemo(() => {
    if (!tabId) {
      return visibleTabs[0]?.value ?? 'general-info';
    }

    const isTabVisible = visibleTabs.some((tab) => tab.value === tabId);
    return isTabVisible ? tabId : (visibleTabs[0]?.value ?? 'general-info');
  }, [tabId, visibleTabs]);

  useEffect(() => {
    if (tsd) {
      const isEditable = [TSDStatus.NOT_SENT, TSDStatus.REJECTED].includes(tsd.generalInfo?.status);
      setDisabled(!isEditable);
    }
  }, [tsd]);

  useEffect(() => {
    if (!tabId) {
      navigate(`${basePath}/general-info`, { replace: true });
    }
  }, [tabId, navigate]);

  const handleTabChange = (newTab: string) => {
    navigate(`${basePath}/${newTab}`);
  };

  const { tabItems, panelItems } = useTSDViewTabs({ visibleTabs });

  if (!tsd) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <ContentLayout>
      <TSDViewHeader tsd={tsd} />
      <Divider />
      <ContentLayout.Content>
        <Tabs h="100%" value={activeTab} onChange={handleTabChange} orientation="vertical" px={50} keepMounted={false} py="lg">
          <Tabs.List>{tabItems}</Tabs.List>
          {panelItems}
        </Tabs>
      </ContentLayout.Content>
    </ContentLayout>
  );
};
